import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase, sbError } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

const ACTIVE_WS_KEY = 'cashdeck-active-ws-id';

function readStoredWsId() {
  try { return localStorage.getItem(ACTIVE_WS_KEY) || null; } catch (_e) { return null; }
}
function writeStoredWsId(id) {
  try {
    if (id) localStorage.setItem(ACTIVE_WS_KEY, id);
    else localStorage.removeItem(ACTIVE_WS_KEY);
  } catch (_e) {}
}

function pickWorkspace(list, preferredId) {
  if (!list || list.length === 0) return null;
  if (preferredId) {
    const hit = list.find(w => w.id === preferredId);
    if (hit) return hit;
  }
  return list[0];
}

const WorkspaceCtx = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [currentId, setCurrentId] = useState(() => readStoredWsId());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);
  const userSeenRef = useRef(false);

  const fetch = useCallback(async () => {
    if (!user) {
      setList([]);
      setCurrentId(null);
      writeStoredWsId(null);
      // Iba ak sme už user-a videli (post-signout). Inicialny mount necháva
      // loading=true aby ProtectedRoute počkal kým auth doresolvne — inak
      // by spadol na /onboarding pri každom reloade.
      if (userSeenRef.current) setLoading(false);
      return;
    }
    userSeenRef.current = true;
    setLoading(true);
    const [{ data: wsData, error: wsErr }, { data: profile }] = await Promise.all([
      supabase.from('workspaces').select('*').eq('user_id', user.id).order('created_at', { ascending: true }),
      supabase.from('users_profile').select('default_workspace_id').eq('id', user.id).maybeSingle(),
    ]);
    if (wsErr) {
      setError(sbError(wsErr));
      setList([]);
      setCurrentId(null);
      setLoading(false);
      return;
    }
    const items = wsData || [];
    setList(items);
    setError(null);
    // ME-02: Ak je stored ID v localStorage, ale firma s týmto ID už neexistuje
    // (vymazaná, prepnutý account), spadni na default_workspace_id zo servera
    // namiesto list[0]. Inak sa zariadenie "stratí" pri zmenách na inom device.
    const stored = readStoredWsId();
    const storedExists = stored && items.some(w => w.id === stored);
    const preferred = (storedExists ? stored : null) || profile?.default_workspace_id || null;
    const picked = pickWorkspace(items, preferred);
    const pickedId = picked?.id || null;
    setCurrentId(pickedId);
    writeStoredWsId(pickedId);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetch(); }, [fetch]);

  const current = list.find(w => w.id === currentId) || list[0] || null;

  const update = useCallback(async (patch) => {
    if (!currentId) return null;
    const { data, error } = await supabase
      .from('workspaces')
      .update(patch)
      .eq('id', currentId)
      .select()
      .single();
    if (error) throw new Error(sbError(error));
    setList(prev => prev.map(w => w.id === data.id ? data : w));
    return data;
  }, [currentId]);

  const create = useCallback(async (payload) => {
    if (!user) throw new Error('Nie si prihlásený.');
    const { data, error } = await supabase
      .from('workspaces')
      .insert({ user_id: user.id, ...payload })
      .select()
      .single();
    if (error) throw new Error(sbError(error));
    await supabase.from('users_profile').update({ default_workspace_id: data.id }).eq('id', user.id);
    setList(prev => [...prev, data]);
    setCurrentId(data.id);
    writeStoredWsId(data.id);
    setVersion(v => v + 1);
    return data;
  }, [user]);

  const switchWorkspace = useCallback(async (id) => {
    if (!id || id === currentId) return;
    if (!list.some(w => w.id === id)) return;
    setCurrentId(id);
    writeStoredWsId(id);
    setVersion(v => v + 1);
    if (user) {
      try {
        await supabase.from('users_profile').update({ default_workspace_id: id }).eq('id', user.id);
      } catch (_e) {}
    }
  }, [currentId, list, user]);

  const value = {
    workspace: current,
    list,
    current,
    loading,
    error,
    version,
    refresh: fetch,
    update,
    create,
    switchWorkspace,
  };

  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) throw new Error('useWorkspace must be used inside WorkspaceProvider');
  return ctx;
}

export function useCloseOnWorkspaceSwitch(active, onSwitch) {
  const { version } = useWorkspace();
  const versionAtOpen = useRef(version);
  const wasActive = useRef(active);

  useEffect(() => {
    if (active && !wasActive.current) {
      // Modal sa práve otvoril — zachyť aktuálnu version.
      versionAtOpen.current = version;
    } else if (!active && wasActive.current) {
      // Modal sa práve zatvoril — synchronizuj versionAtOpen, aby ďalšie
      // open vždy začalo s aktuálnou hodnotou (HI-02).
      versionAtOpen.current = version;
    }
    wasActive.current = active;
  }, [active, version]);

  useEffect(() => {
    if (active && version !== versionAtOpen.current && typeof onSwitch === 'function') {
      // Zaznamenaj že sme už fired-li, aby sa callback nespustil znova
      // pri ďalšom rendere s tou istou version (CR-01 fire-once semantika).
      versionAtOpen.current = version;
      onSwitch();
    }
  }, [active, version, onSwitch]);
}
